import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Card, CardBody, Chip, Spinner } from '@nextui-org/react';
import { BookOpen, ChevronRight } from 'lucide-react';
import infoPageService, { InfoPage } from '../services/infoPageService';

const TEAL = '#3FCED2';

/**
 * /info – Übersicht aller Informationsseiten ("Darts-Wissen") als Cards
 * mit Thumbnail, gruppiert nach Kategorie.
 */
const InfoPagesPage: React.FC = () => {
  const [pages, setPages] = useState<InfoPage[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    infoPageService
      .getAllPages()
      .then((list) => {
        if (!cancelled) setPages(list);
      })
      .catch(() => {
        if (!cancelled) setPages([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Nach Kategorie gruppieren (Seiten ohne Kategorie unter "Weitere Themen")
  const grouped = useMemo(() => {
    const map = new Map<string, InfoPage[]>();
    for (const page of pages) {
      const key = page.category?.trim() || 'Weitere Themen';
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(page);
    }
    return Array.from(map.entries());
  }, [pages]);

  return (
    <div className="min-h-screen bg-zinc-950 pt-20">
      <Helmet>
        <title>Darts-Wissen | DartCloud</title>
        <meta
          name="description"
          content="Darts einfach erklärt: Was ist ein 9-Darter? Wie funktioniert die PDC? Regeln, Begriffe und Hintergründe auf DartCloud."
        />
        <link rel="canonical" href="https://dartcloudapp.de/info" />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2 flex items-center gap-3 text-white">
            <BookOpen size={40} style={{ color: TEAL }} />
            Darts-Wissen
          </h1>
          <p className="text-gray-400">
            Regeln, Begriffe und Hintergründe – verständlich erklärt
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center items-center h-64">
            <Spinner size="lg" style={{ color: TEAL }} />
          </div>
        ) : pages.length === 0 ? (
          <div className="py-16 text-center">
            <BookOpen size={64} className="mx-auto text-gray-600 mb-4" />
            <h3 className="text-xl font-semibold text-gray-400 mb-2">Noch keine Inhalte</h3>
            <p className="text-gray-500">Hier entstehen gerade die ersten Wissens-Seiten.</p>
          </div>
        ) : (
          <div className="space-y-10">
            {grouped.map(([category, categoryPages]) => (
              <section key={category}>
                <h2 className="mb-4 text-xl font-bold text-white">{category}</h2>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {categoryPages.map((page) => {
                    const thumb = infoPageService.buildFileUrl(page.thumbnail || page.heroImage);
                    return (
                      <Link key={page.id || page.slug} to={`/info/${page.slug}`} className="group">
                        <Card className="h-full border border-zinc-800 bg-zinc-900 transition-colors group-hover:border-[#3FCED2]/50">
                          <CardBody className="p-0">
                            <div className="relative aspect-video w-full overflow-hidden bg-zinc-950">
                              {thumb ? (
                                <img
                                  src={thumb}
                                  alt={page.title || ''}
                                  loading="lazy"
                                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                                />
                              ) : (
                                <div className="flex h-full w-full items-center justify-center">
                                  <BookOpen size={40} className="text-zinc-700" />
                                </div>
                              )}
                              {page.category && (
                                <Chip
                                  size="sm"
                                  variant="flat"
                                  className="absolute left-3 top-3 bg-black/60 text-white backdrop-blur"
                                >
                                  {page.category}
                                </Chip>
                              )}
                            </div>
                            <div className="flex flex-1 flex-col p-4">
                              <h3 className="flex items-start gap-1 text-base font-bold text-white">
                                <span className="flex-1">{page.title}</span>
                                <ChevronRight
                                  size={18}
                                  className="mt-0.5 shrink-0 text-gray-500 transition-transform group-hover:translate-x-1"
                                />
                              </h3>
                              {page.intro && (
                                <p className="mt-1.5 text-sm text-gray-400 line-clamp-2">{page.intro}</p>
                              )}
                            </div>
                          </CardBody>
                        </Card>
                      </Link>
                    );
                  })}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default InfoPagesPage;
